import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PropagationOffer } from '../../../../services/profile.service';

interface PropagationPromptProps {
  offer: PropagationOffer;
  /** Friendly name of the tab that was just saved, e.g. "Contact Details". */
  sectionLabel: string;
  saving: boolean;
  onConfirm: (targetJourneyIds: string[]) => void;
  onDismiss: () => void;
}

/**
 * §6.5 — "Apply this change to your other journeys as well?"
 *
 * Shown after a successful save when the investor has other (unlocked) journeys. Nothing
 * is ever copied without an explicit confirmation here, and journeys that are already
 * submitted never appear because the backend filters them out.
 */
export const PropagationPrompt: React.FC<PropagationPromptProps> = ({
  offer,
  sectionLabel,
  saving,
  onConfirm,
  onDismiss,
}) => {
  // Pre-ticked: the common case is "yes, keep my details consistent". Journeys that
  // already have their own values start unticked so we never surprise-overwrite them.
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    setSelected(offer.targets.filter((t) => !t.alreadyOwns).map((t) => t.journeyId));
  }, [offer]);

  // Escape dismisses, like any other modal. Ignored mid-apply so a keypress can't
  // orphan a request that is already in flight.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [saving, onDismiss]);

  if (!offer.targets.length) return null;

  const applyLabel = saving
    ? 'Applying…'
    : selected.length === 0
      ? 'Select a journey'
      : `Apply to ${selected.length} journey${selected.length === 1 ? '' : 's'}`;

  const toggle = (journeyId: string) => {
    setSelected((prev) =>
      prev.includes(journeyId) ? prev.filter((id) => id !== journeyId) : [...prev, journeyId]
    );
  };

  // Portaled to <body>: the prompt lives inside the profile tree, where a transformed
  // ancestor would otherwise make position:fixed resolve against that ancestor instead
  // of the viewport.
  return createPortal(
    <div
      className="propagation-prompt-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Apply this change to your other journeys?"
      // Clicking the backdrop dismisses; clicks inside the panel must not bubble out
      // and close it mid-selection.
      onClick={() => { if (!saving) onDismiss(); }}
    >
      <div className="propagation-prompt" onClick={(e) => e.stopPropagation()}>
        <h4>Apply this change to your other journeys as well?</h4>
        <p className="propagation-prompt__lead">
          You just saved <strong>{sectionLabel}</strong> for this journey. Select any other
          journeys you would like to update with the same details.
        </p>

        {offer.partialCopy && offer.partialCopyNote && (
          <p className="propagation-prompt__note">{offer.partialCopyNote}</p>
        )}

        <ul className="propagation-prompt__targets">
          {offer.targets.map((t) => (
            <li key={t.journeyId}>
              <label>
                <input
                  type="checkbox"
                  checked={selected.includes(t.journeyId)}
                  onChange={() => toggle(t.journeyId)}
                  disabled={saving}
                />
                <span>{t.label}</span>
                {t.alreadyOwns && (
                  <em className="propagation-prompt__warn">
                    already has its own details — ticking this will replace them
                  </em>
                )}
              </label>
            </li>
          ))}
        </ul>

        <div className="propagation-prompt__actions">
          <button type="button" className="btn-outline-primary" onClick={onDismiss} disabled={saving}>
            No, just this journey
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={() => onConfirm(selected)}
            disabled={saving || selected.length === 0}
          >
            {applyLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
